// @flow

import * as React from 'react';

import { AddressField, PhoneField } from '@gandi/react-places';
import { useTranslator } from '@gandi/translate';
import { Base, Button, TogglePill, Switch, Text } from '@gandi/ui';

import {
  CheckboxField,
  Form,
  FormField,
  ToggleGroupField,
  InputTextField,
  SelectField,
  FormGroup,
  decorateControl,
  SwitchField,
} from 'gandi.react-form';
import type { FormComponentProps } from 'gandi.react-form';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { selectHasDomainTransfer } from '../../modules/selectors';

import InvitationCheckboxWrapper from './InvitationCheckboxWrapper';

import styles from './ContactForm.scss';

export type NamedValue = {
  value: any,
  text: string,
};

export type FieldRenderer = (
  // eslint-disable-next-line no-use-before-define
  field: FieldSchema,
  formProps: any,
  children?: Array<React.Node>,
) => React.Node;

export type FieldSchema = {
  name: string,
  type: string,
  required?: boolean,
  label?: string,
  sublabel?: React.Node,
  description?: React.Node,
  renderer?: FieldRenderer,
  values?: Array<NamedValue>,
  isReadOnly?: boolean,
  children?: Array<FieldSchema>,
  showOnly?: string,
};

const multiSwitch =
  (namedValues: Array<NamedValue> = []) =>
  ({ value, onChange }: { value: any, onChange: any }) => {
    const valuesSet = new Set(value || []);

    return (
      <>
        {namedValues.map(namedValue => (
          <Switch
            key={namedValue.value}
            name={namedValue.value}
            checked={valuesSet.has(namedValue.value)}
            onChange={event => {
              if (event.target.checked) valuesSet.add(namedValue.value);
              else valuesSet.delete(namedValue.value);
              onChange([...valuesSet]);
            }}
          >
            <Text weight="semibold">{namedValue.text}</Text>
          </Switch>
        ))}
      </>
    );
  };

export const multiSelectRenderer = (field: FieldSchema): React.Node => (
  <FormField
    key={field.name}
    name={field.name}
    label={field.label}
    component={decorateControl(multiSwitch(field.values), FormGroup)}
  />
);

export const togglePillGroupRenderer = (field: FieldSchema): React.Node => (
  <ToggleGroupField
    key={field.name}
    name={field.name}
    label={field.label}
    disabled={field.isReadOnly}
  >
    {(field.values || []).map(v => (
      <TogglePill key={v.value} value={v.value}>
        {v.text}
      </TogglePill>
    ))}
  </ToggleGroupField>
);

export const showSelectedPillGroupRenderer = (
  field: FieldSchema,
): React.Node => (
  <ToggleGroupField
    key={field.name}
    name={field.name}
    label={field.label}
    disabled={field.isReadOnly}
  >
    {(field.values || []).map(v => {
      if (field.showOnly === v.value) {
        return (
          <TogglePill key={v.value} value={v.value}>
            {v.text}
          </TogglePill>
        );
      }
      return null;
    })}
  </ToggleGroupField>
);

const checkboxRenderer = (field: FieldSchema): React.Node => (
  <CheckboxField
    key={field.name}
    name={field.name}
    label={field.label}
    help={field.description}
    required={field.required}
  />
);

export const switchRenderer = (field: FieldSchema): React.Node => (
  <SwitchField
    key={field.name}
    name={field.name}
    label={field.label}
    help={field.description}
    required={field.required}
    className={styles.switchWithSublabel}
  >
    {field.sublabel}
  </SwitchField>
);

// The supported field types.
const defaultRenderers: {
  +[string]: FieldRenderer,
  ...
} = {
  form__root: (field, formProps, children: Array<React.Node> = []) => children,
  form__section: (field, formProps, children) => (
    <Base
      key={field.name}
      className="wrapper-md wrapper-plain"
      marginBottom="base"
    >
      {children}
    </Base>
  ),
  boolean: checkboxRenderer,
  string: field => (
    <InputTextField
      key={field.name}
      name={field.name}
      label={field.label}
      required={field.required}
    />
  ),
  oneOf: field => (
    <SelectField
      key={field.name}
      name={field.name}
      label={field.label}
      values={field.values}
    />
  ),
  email: field => (
    <InputTextField
      key={field.name}
      name={field.name}
      label={field.label}
      required={field.required}
      type="email"
    />
  ),
  address: (field, formProps) => (
    <AddressField
      forceMandatorySubdivision
      key={field.name}
      form={formProps.form}
      change={() => {}}
    />
  ),
  phone: field => (
    <PhoneField
      key={field.name}
      name={field.name}
      label={field.label}
      required={field.required}
    />
  ),
};

export function visitFormNodes(node: any, transform: any => any): any {
  const newNode = transform(node);

  if (newNode.children) {
    newNode.children = newNode.children.map(child =>
      visitFormNodes(child, transform),
    );
  }

  return newNode;
}

const renderFormContent = (
  form: FieldSchema,
  formProps: FormComponentProps,
): any => {
  const renderer = form.renderer || defaultRenderers[form.type];
  return (
    renderer &&
    renderer(
      form,
      formProps,
      (form.children || []).map(child => renderFormContent(child, formProps)),
    )
  );
};

export type Props = {
  formProps: FormComponentProps,
  formView: any,
  cancelUrl: string,
  cancelLabel: string,
  submitLabel: string,
  isWorking: boolean,
  canWriteContactBook: boolean,
};

export default function ContactForm({
  formProps,
  formView,
  cancelUrl,
  cancelLabel,
  submitLabel,
  isWorking,
  canWriteContactBook,
}: Props): React.Node {
  const { __ } = useTranslator();

  const hasDomainTransfer = useAppSelector(selectHasDomainTransfer);

  return (
    <Base marginBottom="base">
      <Form
        id={formProps.form}
        method="post"
        noValidate
        onSubmit={formProps.handleSubmit}
      >
        <div className="form-stacked">
          {renderFormContent(formView, formProps)}
        </div>
        {hasDomainTransfer && (
          <Base marginBottom="base">
            <InvitationCheckboxWrapper>
              <CheckboxField name="send_invitation">
                {__(
                  'Email an invitation to the contact, allowing them to manage the domain.',
                )}
              </CheckboxField>
            </InvitationCheckboxWrapper>
          </Base>
        )}
        <div className="form-stacked">
          <SwitchField
            defaultChecked={canWriteContactBook}
            disabled={!canWriteContactBook}
            key="saveToContactbook"
            name="saveToContactbook"
            className={styles.switchWithSublabel}
          >
            {__('Save to contact book')}
          </SwitchField>
        </div>

        <Base flex flexWrap>
          <Button to={cancelUrl} kind="outline" grouped disabled={isWorking}>
            {cancelLabel}
          </Button>
          <Button form={formProps.form} grouped loading={isWorking}>
            {submitLabel}
          </Button>
        </Base>
      </Form>
    </Base>
  );
}
